import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCmsRoles } from "@/hooks/useCmsRoles";
import { useSciPreview, fmtDate } from "@/components/sci/useSciPreview";
import { SciLoadError } from "@/components/sci/SciNotFound";
import { resetGuidesCache } from "@/lib/subjectGuides";
import { useFastFitGuide } from "@/components/sci/student/useFastFitGuide";
import { resultsHref } from "@/lib/fastFitGuideContext";

// Arrived from a Fast Fit guide: keep the context so guide links bring the Fast Fit bar back.
function FastFitDirectoryBar({ ff }) {
  const fam = (ff.view.explored || []).find((f) => f.slug === ff.params.family);
  return (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[#7C3AED]/40 bg-white p-3 text-sm" data-testid="ff-directory-bar">
      <span className="text-slate-700">You're browsing from Fast Fit{fam ? <> · exploring <b className="font-semibold text-slate-900">{fam.family}</b></> : null}. Guides you open keep your Fast Fit bar.</span>
      <Link to={resultsHref(ff.params.family)} className="font-medium text-[#7C3AED] hover:underline" data-testid="ff-directory-return">← Back to my families &amp; results</Link>
    </div>
  );
}

function GuideCard({ g, suffix }) {
  return (
    <li className="flex flex-col rounded-lg border border-[#d9d2c3] bg-white p-5 transition-shadow hover:shadow-md" data-testid={`subjects-card-${g.slug}`}>
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-editorial text-xl text-[#12312b]">{g.name}</h3>
        {g.fast_match && <span className="shrink-0 rounded bg-[#e6efe9] px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-[#175d51]">In Fast Match</span>}
      </div>
      <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{g.description}</p>
      <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-sm font-semibold">
        <Link to={`/subjects/${g.slug}${suffix}`} className="text-[#175d51] underline-offset-4 hover:underline" data-testid={`subjects-read-${g.slug}`}>Read the guide →</Link>
        <Link to={`/advanced?subject=${encodeURIComponent(g.name)}`} className="text-slate-700 underline-offset-4 hover:underline" data-testid={`subjects-courses-${g.slug}`}>Matching courses</Link>
      </div>
    </li>
  );
}

const failed = () => toast.error("The request failed; nothing was changed.");

function AdminPublishing({ onChange }) {
  const [data, setData] = useState(null);
  const load = () => Promise.all([api.get("/admin/subject-guides"), api.get("/admin/publishing-connections")])
    .then(([g, c]) => setData({ guides: g.data.guides, batches: g.data.batches, connections: c.data.connections })).catch(() => setData(null));
  useEffect(() => { load(); }, []);
  if (!data) return null;
  const revoke = (id) => api.delete(`/admin/publishing-connections/${encodeURIComponent(id)}`)
    .then(() => { toast.success("Connector access revoked"); load(); }).catch(failed);
  const restore = (g) => api.post(`/admin/subject-guides/${g.slug}/restore`, { expected_fingerprint: g.live_fingerprint })
    .then((r) => { if (r.data.result !== "published") return failed(); toast.success(`${g.name} is published again`); resetGuidesCache(); load(); onChange(); }).catch(failed);
  const withdrawn = data.guides.filter((g) => g.status !== "published");
  return (
    <section className="mt-12 rounded-lg border border-slate-300 bg-white p-5 text-sm" data-testid="subjects-admin">
      <h2 className="font-semibold text-slate-900">Publishing (admins only)</h2>
      <p className="mt-1 text-slate-600">ChatGPT connections: {data.connections.length === 0 ? "none" : ""}</p>
      <ul>{data.connections.map((c) => (
        <li key={c.client_id} className="flex gap-3">{c.client_name} · as {c.authorized_as} · since {fmtDate(c.since)}
          <button type="button" className="text-red-700 underline" onClick={() => revoke(c.client_id)} data-testid={`subjects-admin-revoke-${c.client_id}`}>Revoke</button></li>))}</ul>
      <p className="mt-3 text-slate-600">Withdrawn guides: {withdrawn.length === 0 ? "none" : ""}</p>
      <ul>{withdrawn.map((g) => (
        <li key={g.slug}>{g.name} (v{g.live_version}) <button type="button" className="underline" onClick={() => restore(g)} data-testid={`subjects-admin-restore-${g.slug}`}>Restore</button></li>))}</ul>
      <p className="mt-3 text-slate-600">Recent uploads:</p>
      <ul>{data.batches.map((b) => <li key={b.batch_id}>{fmtDate(b.at)} · {b.origin}{b.dry_run ? " (check only)" : ""} · {Object.entries(b.summary).filter(([, n]) => n).map(([k, n]) => `${n} ${k}`).join(", ")}</li>)}</ul>
    </section>
  );
}

export default function SubjectsDirectory() {
  const { loading, data, error, retry, refresh } = useSciPreview("/subjects");
  const { isAdmin } = useCmsRoles();
  const [q, setQ] = useState("");
  const { search } = useLocation();
  const ff = useFastFitGuide(search, "directory");
  const ffOn = ff.status === "ready" && ff.params?.family;
  const suffix = ffOn ? `?from=fast-fit&family=${encodeURIComponent(ff.params.family)}` : "";
  const families = useMemo(() => {
    const out = new Map();
    (data?.guides || []).filter((g) => !q || `${g.name} ${g.family}`.toLowerCase().includes(q.toLowerCase()))
      .forEach((g) => out.set(g.family, [...(out.get(g.family) || []), g]));
    return [...out.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [data, q]);
  if (error) return <SciLoadError onRetry={retry} />;
  const n = data?.counts.published || 0;
  return (
    <div className="min-h-screen bg-[#f7f4ee]" data-testid="subjects-directory">
      <Helmet>
        <title>Subject guides | Which University Advisor</title>
        <meta name="description" content="Student guides to university subjects: what you study, how degrees differ across eight countries, careers, money and how AI may change the work." />
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
        {ffOn && <FastFitDirectoryBar ff={ff} />}
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#175d51]">Subject guides</p>
        <h1 className="font-editorial mt-2 text-4xl text-[#12312b] sm:text-5xl">Find out what a subject is really like</h1>
        <p className="mt-4 max-w-2xl text-base text-slate-700" data-testid="subjects-count">
          {loading ? "Loading guides…" : n === 0 ? "The first guides are being prepared. Fast Match already covers 57 subjects; guides appear here as soon as each one is published."
            : `${n} published ${n === 1 ? "guide" : "guides"}. Each one covers five steps: a quick look, a taste of the work, the degree, eight countries, and careers and money.`}
        </p>
        <div className="mt-4 flex flex-wrap gap-4 text-sm font-semibold">
          <Link to="/beta/course-fit" className="text-[#175d51] underline-offset-4 hover:underline" data-testid="subjects-fast-match-link">Not sure which subject? Try Fast Match →</Link>
        </div>
        {n >= 12 && (
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search subjects"
            className="mt-8 w-full max-w-md rounded-md border border-slate-300 px-3 py-2" aria-label="Search subjects" data-testid="subjects-search" />
        )}
        {families.map(([fam, guides]) => (
          <section key={fam} className="mt-10" data-testid={`subjects-family-${guides[0].family_code}`}>
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">{fam}</h2>
            <ul className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{guides.map((g) => <GuideCard key={g.slug} g={g} suffix={suffix} />)}</ul>
          </section>
        ))}
        {isAdmin && <AdminPublishing onChange={refresh} />}
      </div>
    </div>
  );
}
